/* ================================================================
   PublicLayout.tsx — Header unique + Footer complet
   Utilisé par AboutPage, NewsPage (et toute page publique).
   Design aligné sur MarketingPage : blue-900 / amber-400 / useLang
   Date: 2026-09-12
   ================================================================ */

import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom'
import {
  GraduationCap, Menu, X, Newspaper, Info, Home,
  LayoutGrid, MapPin, Phone, Mail, Globe,
  Linkedin, Facebook, Twitter,
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'

/* ------------------------------------------------------------
 * Page visit tracking (best-effort, ne bloque jamais la nav)
 * ---------------------------------------------------------- */
function recordPageVisit(path: string, category: string = 'public') {
  // Désactivé : l'endpoint /analytics/page-visit n'existe pas encore dans
  // le backend (404 en boucle). Réactivez-le quand la route sera créée,
  // ou remplacez par votre vrai endpoint de tracking.
  void path; void category;
}

/* ------------------------------------------------------------
 * PublicNavbar — le SEUL header des pages publiques
 * Liens: Accueil, Actualités, À propos, Modules, Connexion, Démo
 * (plus de Événements / Forum / Historique)
 * ---------------------------------------------------------- */
export function PublicNavbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { lang, toggleLang } = useLang()
  const isFr = lang === 'fr'
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    recordPageVisit(location.pathname)
  }, [location.pathname])

  const handleNavClick = (path: string) => {
    setMobileMenuOpen(false)
    recordPageVisit(path)
    navigate(path)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false)
    if (location.pathname !== '/') {
      navigate(`/#${id}`)
      return
    }
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const navLinks = [
    { icon: Home, fr: 'Accueil', en: 'Home', action: () => handleNavClick('/') },
    { icon: Newspaper, fr: 'Actualités', en: 'News', action: () => handleNavClick('/news') },
    { icon: Info, fr: 'À propos', en: 'About', action: () => handleNavClick('/about') },
    { icon: LayoutGrid, fr: 'Modules', en: 'Modules', action: () => scrollToSection('modules') },
  ]

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-blue-900/95 backdrop-blur-md shadow-lg py-3'
          : 'bg-blue-900 py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <button
          className="flex items-center gap-3"
          onClick={() => handleNavClick('/')}
        >
          <div className="w-10 h-10 bg-amber-400 rounded-lg flex items-center justify-center shadow-lg">
            <GraduationCap className="w-6 h-6 text-blue-900" />
          </div>
          <div className="flex flex-col text-left">
            <span className="text-white font-bold text-lg leading-tight tracking-tight">
              MY HELPER <span className="text-amber-400">School</span>
            </span>
            <span className="text-white/60 text-[10px] uppercase tracking-wider leading-tight hidden sm:block">
              {isFr ? 'Solution Digitale Tout-en-Un' : 'All-in-One Digital Solution'}
            </span>
          </div>
        </button>

        {/* Nav desktop */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <button
              key={link.fr}
              onClick={link.action}
              className="text-sm font-medium text-white/80 hover:text-amber-400 transition-colors flex items-center gap-2"
            >
              <link.icon className="w-4 h-4" />
              {isFr ? link.fr : link.en}
            </button>
          ))}
        </nav>

        {/* Actions desktop */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleLang}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white/80 hover:text-white border border-white/20 hover:border-white/40 transition-all"
          >
            {isFr ? 'FR' : 'EN'}
          </button>
          <button
            onClick={() => handleNavClick('/login')}
            className="text-sm font-medium text-white/80 hover:text-white transition-colors"
          >
            {isFr ? 'Connexion' : 'Login'}
          </button>
          <button
            onClick={() => scrollToSection('demo')}
            className="px-5 py-2.5 bg-amber-400 hover:bg-amber-300 text-blue-900 text-sm font-bold rounded-lg shadow-lg shadow-amber-400/20 transition-all hover:scale-105"
          >
            {isFr ? 'Demander une démo' : 'Request a demo'}
          </button>
        </div>

        {/* Burger mobile */}
        <button
          className="md:hidden p-2 text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Menu mobile */}
      <div
        className={`md:hidden bg-blue-900 border-t border-white/10 overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <button
              key={link.fr}
              onClick={link.action}
              className="flex items-center gap-2 w-full text-left text-white/80 hover:text-amber-400 py-2.5 text-sm font-medium"
            >
              <link.icon className="w-4 h-4" />
              {isFr ? link.fr : link.en}
            </button>
          ))}
          <div className="pt-3 border-t border-white/10 flex flex-col gap-3">
            <button onClick={toggleLang} className="text-left text-white/60 text-sm">
              {isFr ? '🇫🇷 Français' : '🇬🇧 English'}
            </button>
            <button
              onClick={() => handleNavClick('/login')}
              className="text-left text-white/80 text-sm font-medium"
            >
              {isFr ? 'Connexion' : 'Login'}
            </button>
            <button
              onClick={() => scrollToSection('demo')}
              className="w-full py-3 bg-amber-400 text-blue-900 font-bold rounded-lg text-center"
            >
              {isFr ? 'Demander une démo' : 'Request a demo'}
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

/* ------------------------------------------------------------
 * PublicFooter — 4 colonnes, contact complet, branding MINESEC
 * ---------------------------------------------------------- */
export function PublicFooter() {
  const { lang } = useLang()
  const isFr = lang === 'fr'
  const navigate = useNavigate()
  const year = new Date().getFullYear()

  const quickLinks = [
    { fr: 'Accueil', en: 'Home', path: '/' },
    { fr: 'Actualités', en: 'News', path: '/news' },
    { fr: 'À propos', en: 'About', path: '/about' },
    { fr: 'Connexion', en: 'Login', path: '/login' },
  ]

  const modules = [
    { fr: 'Élèves & Enseignants', en: 'Students & Teachers' },
    { fr: 'Notes & Bulletins', en: 'Grades & Reports' },
    { fr: 'Emploi du temps', en: 'Timetable' },
    { fr: 'Finances & Budget', en: 'Finance & Budget' },
    { fr: 'Discipline & Assiduité', en: 'Discipline & Attendance' },
    { fr: 'Rapports MINESEC', en: 'MINESEC Reports' },
  ]

  return (
    <footer className="bg-blue-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Col 1 — Marque */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-400 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-blue-900" />
              </div>
              <span className="font-bold text-lg">
                MY HELPER <span className="text-amber-400">School</span>
              </span>
            </div>
            <p className="text-blue-200/70 text-sm leading-relaxed mb-4">
              {isFr
                ? "Solution digitale tout-en-un pour la gestion des établissements scolaires secondaires, conforme aux exigences du MINESEC."
                : 'All-in-one digital solution for secondary school management, compliant with MINESEC requirements.'}
            </p>
            <span className="inline-block px-2 py-1 bg-amber-400/10 border border-amber-400/30 rounded text-amber-400 text-xs font-semibold">
              ✦ Conforme MINESEC
            </span>
          </div>

          {/* Col 2 — Liens rapides */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-amber-400 mb-4">
              {isFr ? 'Liens rapides' : 'Quick Links'}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.path}>
                  <button
                    onClick={() => navigate(l.path)}
                    className="text-blue-200/70 hover:text-amber-400 text-sm transition-colors"
                  >
                    {isFr ? l.fr : l.en}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => navigate('/#demo')}
                  className="text-blue-200/70 hover:text-amber-400 text-sm transition-colors"
                >
                  {isFr ? 'Demander une démo' : 'Request a demo'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/#modules')}
                  className="text-blue-200/70 hover:text-amber-400 text-sm transition-colors"
                >
                  {isFr ? 'Modules' : 'Modules'}
                </button>
              </li>
            </ul>
          </div>

          {/* Col 3 — Modules */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-amber-400 mb-4">
              {isFr ? 'Modules' : 'Modules'}
            </h4>
            <ul className="space-y-3">
              {modules.map((m) => (
                <li key={m.en} className="text-blue-200/70 text-sm">
                  {isFr ? m.fr : m.en}
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Contact */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider text-amber-400 mb-4">
              {isFr ? 'Contact' : 'Contact'}
            </h4>
            <ul className="space-y-3 text-sm text-blue-200/70">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                Yaoundé, Cameroun
              </li>
              <li>
                <a href="tel:+237697543563" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                  <Phone className="w-4 h-4 text-amber-400" /> +237 697 54 35 63
                </a>
              </li>
              <li>
                <a href="tel:+237679738560" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                  <Phone className="w-4 h-4 text-amber-400" /> +237 679 73 85 60
                </a>
              </li>
              <li>
                <a href="mailto:contact@myhelperschool.com" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                  <Mail className="w-4 h-4 text-amber-400" /> contact@myhelperschool.com
                </a>
              </li>
              <li>
                <a href="https://www.myhelperschool.cm" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                  <Globe className="w-4 h-4 text-amber-400" /> www.myhelperschool.cm
                </a>
              </li>
            </ul>
            <div className="flex gap-3 mt-5">
              <a href="#" aria-label="LinkedIn"
                className="w-10 h-10 bg-white/10 hover:bg-amber-400 rounded-lg flex items-center justify-center transition-colors group">
                <Linkedin className="w-5 h-5 text-white group-hover:text-blue-900" />
              </a>
              <a href="#" aria-label="Facebook"
                className="w-10 h-10 bg-white/10 hover:bg-amber-400 rounded-lg flex items-center justify-center transition-colors group">
                <Facebook className="w-5 h-5 text-white group-hover:text-blue-900" />
              </a>
              <a href="#" aria-label="Twitter"
                className="w-10 h-10 bg-white/10 hover:bg-amber-400 rounded-lg flex items-center justify-center transition-colors group">
                <Twitter className="w-5 h-5 text-white group-hover:text-blue-900" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Barre du bas */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-blue-200/50">
              © {year} My Helper School — {isFr ? 'Tous droits réservés. Conforme MINESEC.' : 'All rights reserved. My Helper School Compliant.'}
            </p>
            <div className="flex gap-6">
              <button className="text-xs text-blue-200/50 hover:text-amber-400 transition-colors">
                {isFr ? 'Mentions légales' : 'Legal Notice'}
              </button>
              <button className="text-xs text-blue-200/50 hover:text-amber-400 transition-colors">
                {isFr ? 'Politique de confidentialité' : 'Privacy Policy'}
              </button>
              <button className="text-xs text-blue-200/50 hover:text-amber-400 transition-colors">
                {isFr ? "Conditions d'utilisation" : 'Terms of Use'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

/* ------------------------------------------------------------
 * PublicLayout — enveloppe toute page publique
 * Usage : <PublicLayout> ...contenu... </PublicLayout>
 * ---------------------------------------------------------- */
export default function PublicLayout({ children }: { children?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 flex flex-col">
      <PublicNavbar />
      {/* Mode 1 : composant wrapper (<PublicLayout>...</PublicLayout>)
          Mode 2 : route layout React Router (<Route element={<PublicLayout />}>) */}
      <main className="flex-1">{children ?? <Outlet />}</main>
      <PublicFooter />
    </div>
  )
}